/*
 * Behavior: a paste that looks automated. Checks, in order:
 *   1. The paste event is untrusted (dispatched by a script) => bot.
 *   2. No real gesture preceded it. Every human paste follows either a Ctrl/Cmd+V
 *      keydown or a right-click (contextmenu -> Paste). A paste with neither
 *      recently => bot.
 *   3. Backup timing: the paste lands sooner than a human could act after the
 *      field gained focus (< 150ms).
 *
 * Only trusted key/menu gestures count, so a script cannot fake a gesture to slip
 * past check 2. Reports via the shared callback from useBotDetection.
 */
import { useEffect, useRef } from 'react'
import { TrackBotTrafficAction } from 'lib/bots/trackBotTraffic'

// Fastest time (in milliseconds) between focusing a field and a human paste.
const MIN_HUMAN_FOCUS_TO_PASTE_MS = 150
// How long before the paste a Ctrl/Cmd+V or right-click still counts as its
// gesture (a right-click menu can take a moment before Paste is chosen).
const GESTURE_WINDOW_MS = 5000

const isTextEntryElement = (element: EventTarget | null): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false
  }
  return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable
}

const usePasteBehaviorBotCheck = (report: (action: TrackBotTrafficAction) => void): void => {
  // All times below are in milliseconds since page load.
  const lastFocusTimeMs = useRef(0)
  const lastPasteShortcutMs = useRef(0)
  const lastContextMenuMs = useRef(0)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    // Remember when an editable text field gains focus.
    const handleFocusIn = (event: FocusEvent) => {
      if (isTextEntryElement(event.target)) {
        lastFocusTimeMs.current = performance.now()
      }
    }

    // Remember a real Ctrl/Cmd+V keydown (the keyboard paste gesture).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.isTrusted) {
        return
      }
      if ((event.ctrlKey || event.metaKey) && (event.key === 'v' || event.key === 'V')) {
        lastPasteShortcutMs.current = performance.now()
      }
    }

    // Remember a real right-click (the menu paste gesture).
    const handleContextMenu = (event: MouseEvent) => {
      if (event.isTrusted) {
        lastContextMenuMs.current = performance.now()
      }
    }

    const handlePaste = (event: ClipboardEvent) => {
      // Check 1: a paste a script dispatched itself is not trusted.
      if (!event.isTrusted) {
        report('keyboard')
        return
      }

      const now = performance.now()

      // Check 2: a real paste follows a Ctrl/Cmd+V keydown or a right-click menu.
      const hadPasteShortcut = now - lastPasteShortcutMs.current < GESTURE_WINDOW_MS
      const hadContextMenu = now - lastContextMenuMs.current < GESTURE_WINDOW_MS
      if (!hadPasteShortcut && !hadContextMenu) {
        report('keyboard')
        return
      }

      // Check 3 (backup): faster than a human could act after focusing the field.
      if (lastFocusTimeMs.current !== 0 && now - lastFocusTimeMs.current < MIN_HUMAN_FOCUS_TO_PASTE_MS) {
        report('keyboard')
      }
    }

    // A focus captured before the tab was hidden is stale; clear it so a paste
    // after the user comes back is not measured against an old time.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastFocusTimeMs.current = 0
      }
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('keydown', handleKeyDown, { capture: true })
    document.addEventListener('contextmenu', handleContextMenu, { capture: true })
    document.addEventListener('paste', handlePaste)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('keydown', handleKeyDown, { capture: true })
      document.removeEventListener('contextmenu', handleContextMenu, { capture: true })
      document.removeEventListener('paste', handlePaste)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])
}

export default usePasteBehaviorBotCheck
